import { GameConstants } from "../../../GameConstants";

export enum AvatarExpression {
    NORMAL = "normal",
    THINKING = "thinking",
    TIRED = "tired"
}

export class Avatar extends Phaser.GameObjects.Container {

    private static readonly TRANSITION_DURATION = 350;

    private glowFrame: Phaser.GameObjects.Graphics;
    private readonly expressionImages = new Map<AvatarExpression, Phaser.GameObjects.Image>();
    private expression = AvatarExpression.NORMAL;
    private turnPulse?: Phaser.Tweens.Tween;
    public readonly nameLabel?: Phaser.GameObjects.Text;

    constructor(
        scene: Phaser.Scene, private avatarId: number, showName = false, turnFrameInset = 0,
        profileId = avatarId
    ) {

        super(scene);


        const w = 119 - turnFrameInset * 2;
        const h = 136 - turnFrameInset * 2;
        const r = 22 - turnFrameInset;

        this.glowFrame = new Phaser.GameObjects.Graphics(scene);
        this.glowFrame.fillStyle(0xffff00, 1);
        this.glowFrame.fillRoundedRect(-w / 2, -h / 2, w, h, r);
        this.glowFrame.visible = false;
        this.add(this.glowFrame);

        this.getOrCreateExpression(AvatarExpression.NORMAL).setAlpha(1);
        this.getOrCreateExpression(AvatarExpression.THINKING);

        const name = GameConstants.BOT_PROFILES.find(profile => profile.id === profileId)?.name;
        if (showName && name) {
            const nameLabel = new Phaser.GameObjects.Text(scene, 0, 65, name, {
                fontFamily: "Baloo2-Bold",
                fontSize: "28px",
                color: "#ffffff",
                stroke: "#000000",
                strokeThickness: 4
            });
            nameLabel.setOrigin(.5, 0);
            this.add(nameLabel);
            this.nameLabel = nameLabel;
        }
    }

    public setAvatarId(avatarId: number): void {

        this.avatarId = avatarId;
        this.expressionImages.forEach((image, expression) => {
            const frame = expression === AvatarExpression.NORMAL
                ? `avatar_${avatarId}` : `avatar_${avatarId}_${expression}`;
            image.setFrame(frame);
        });
    }

    public setExpression(expression: AvatarExpression): void {

        if (this.expression === expression) {
            return;
        }
        const selected = this.getOrCreateExpression(expression);
        this.expression = expression;
        this.expressionImages.forEach(image => {
            this.scene.tweens.killTweensOf(image);
            this.scene.tweens.add({
                targets: image,
                alpha: image === selected ? 1 : 0,
                duration: Avatar.TRANSITION_DURATION,
                ease: Phaser.Math.Easing.Cubic.Out
            });
        });
    }

    private getOrCreateExpression(expression: AvatarExpression): Phaser.GameObjects.Image {

        const existing = this.expressionImages.get(expression);
        if (existing) {
            return existing;
        }
        const frame = expression === AvatarExpression.NORMAL
            ? `avatar_${this.avatarId}` : `avatar_${this.avatarId}_${expression}`;
        if (!this.scene.textures.getFrame("avatar_atlas", frame)) {
            throw new Error(`Missing atlas frame: ${frame}`);
        }
        const image = new Phaser.GameObjects.Image(this.scene, 0, 0, "avatar_atlas", frame)
            .setScale(.625).setAlpha(0);
        this.addAt(image, this.expressionImages.size + 1);
        this.expressionImages.set(expression, image);
        return image;
    }

    public destroy(fromScene?: boolean): void {

        if (!this.scene) {
            return;
        }

        this.expressionImages.forEach(image => this.scene.tweens.killTweensOf(image));
        this.stopTurnPulse();
        super.destroy(fromScene);
    }

    public activate(pulsing = false): void {
    
        this.glowFrame.visible = true;
        if (!pulsing) {
            this.stopTurnPulse();
        } else if (!this.turnPulse) {
            this.glowFrame.setAlpha(1);
            this.turnPulse = this.scene.tweens.add({
                targets: this.glowFrame,
                alpha: .7,
                duration: 600,
                ease: "Sine.InOut",
                yoyo: true,
                repeat: -1
            });
        }
    }

    public deactivate(): void {
        
        this.glowFrame.visible = false;
        this.stopTurnPulse();
    }

    private stopTurnPulse(): void {

        this.scene.tweens.killTweensOf(this.glowFrame);
        this.turnPulse = undefined;
        this.glowFrame.setAlpha(1);
    }
}
